import React, { useState } from "react";
import styled from "@emotion/styled";
import useSWR from "swr";
import ServiceWrapper from "../components/ServiceWrapper";
import { api } from "../utils/api";
import { track } from "../utils/track";

/**
 * 친구 초대 (2026-10-17 사장님). 링크를 카톡 등으로 보내고, 그 링크로 가입한 친구가 구독하면 쿠폰 알림을 받는다.
 * 서버: api ReferralController, 쿠폰 알림: Console/Commands/RewardReferrals
 */
type Me = { link: string; invited: number; subscribed: number; coupons: { friend: string; date: string }[] };

const SHARE_TEXT = "기타·우쿨렐레 코드 악보, 코드스코어에서 같이 쳐요! 원곡에 맞춰 코드가 따라 움직여요.";

const Invite: React.FC = () => {
  const { data } = useSWR<Me>("/referral/me", (url: string) => api.get(url).then((r) => r.data.data));
  const [msg, setMsg] = useState("");

  const copy = async (link: string) => {
    try {
      await navigator.clipboard.writeText(`${SHARE_TEXT}\n${link}`);
    } catch (e) {
      const t = document.createElement("textarea");
      t.value = `${SHARE_TEXT}\n${link}`;
      document.body.appendChild(t);
      t.select();
      document.execCommand("copy");
      document.body.removeChild(t);
    }
    setMsg("링크를 복사했어요. 카카오톡 대화방에 붙여 넣어 보내 주세요.");
  };

  const share = async () => {
    if (!data) return;
    track("invite:share");
    const nav: any = navigator;
    if (nav.share) {
      try {
        await nav.share({ title: "코드스코어", text: SHARE_TEXT, url: data.link });
        return;
      } catch (e) {
        if ((e as any)?.name === "AbortError") return;
      }
    }
    copy(data.link);
  };

  return (
    <ServiceWrapper isFooterRendered={false}>
      <Page>
        <h1>친구 초대</h1>
        <Lead>
          아래 단추로 친구에게 초대 링크를 보내 주세요. 친구가 이 링크로 가입하면 추천인에 내 이름이 들어가고,
          그 친구가 구독하면 <b>1개월 무료 쿠폰</b>을 알림으로 드려요.
        </Lead>
        <Go type="button" onClick={share} disabled={!data}>카카오톡 등으로 링크 보내기</Go>
        {data && (
          <LinkRow>
            <span>{data.link}</span>
            <button type="button" onClick={() => { track("invite:copy"); copy(data.link); }}>복사</button>
          </LinkRow>
        )}
        {msg && <Msg>{msg}</Msg>}
        <Stats>
          <div><b>{data ? data.invited : "-"}</b><span>가입한 친구</span></div>
          <div><b>{data ? data.subscribed : "-"}</b><span>구독한 친구</span></div>
          <div><b>{data ? data.coupons.length : "-"}</b><span>받은 쿠폰</span></div>
        </Stats>
        {data && data.coupons.length > 0 && (
          <Coupons>
            {data.coupons.map((c, i) => (
              <li key={i}><span>1개월 무료 쿠폰</span><em>{c.friend}님 구독 · {c.date}</em></li>
            ))}
          </Coupons>
        )}
        <Note>쿠폰은 다음 결제 때 한 달 무료로 적용돼요. 친구 한 명당 한 장이고, 내 휴대폰 번호로 다시 가입한 경우는 빠져요.</Note>
      </Page>
    </ServiceWrapper>
  );
};

export default Invite;

const Page = styled.div`
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 16px 16px 110px;
  color: #f3f4f6;
  word-break: keep-all;
  & > h1 {
    margin: 0 0 12px;
    font-size: 24px;
    font-weight: 700;
  }
`;

const Lead = styled.p`
  margin: 0 0 20px;
  font-size: 15px;
  line-height: 1.7;
  color: #c4c9d4;
  & b {
    color: #ffb547;
  }
`;

const Go = styled.button`
  width: 100%;
  height: 52px;
  border: 0;
  border-radius: 14px;
  background: #ffb547;
  color: #0d0e12;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
  }
`;

const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding: 10px 12px;
  border: 1px solid #2a2e38;
  border-radius: 12px;
  background: #16181e;
  & span {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    color: #a3a9b5;
  }
  & button {
    flex: none;
    height: 34px;
    padding: 0 14px;
    border: 1px solid #2a2e38;
    border-radius: 10px;
    background: transparent;
    color: #f3f4f6;
    font-size: 14px;
    cursor: pointer;
  }
`;

const Msg = styled.p`
  margin: 10px 0 0;
  font-size: 14px;
  color: #ffb547;
`;

const Stats = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 24px;
  & > div {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 14px 4px;
    border: 1px solid #2a2e38;
    border-radius: 14px;
    background: #16181e;
  }
  & b {
    font-size: 22px;
  }
  & span {
    font-size: 13px;
    color: #a3a9b5;
  }
`;

const Coupons = styled.ul`
  list-style: none;
  margin: 16px 0 0;
  padding: 0;
  & li {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 12px 14px;
    border: 1px dashed #ffb547;
    border-radius: 12px;
    & + li {
      margin-top: 8px;
    }
  }
  & span {
    font-weight: 700;
    color: #ffb547;
  }
  & em {
    font-style: normal;
    font-size: 13px;
    color: #a3a9b5;
  }
`;

const Note = styled.p`
  margin: 20px 0 0;
  font-size: 13px;
  line-height: 1.6;
  color: #7c8392;
`;
